import React from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { AlertTriangle, FileText, Eye, Trash2 } from "lucide-react";

/**
 * ITTicketsTable — Tabla de tickets internos IT + diálogo de confirmación de borrado.
 *
 * Extraída 1:1 desde `pages/ITTicketsPage.js`. Renderiza los tickets ya filtrados (número,
 * tipo, título, creador, asignado, prioridad, estado, fecha) con acciones de fila (ver
 * detalle y eliminar con guard RBAC `canDelete`). Incluye el `AlertDialog` de borrado.
 *
 * @param {Array}    props.filteredTickets    Tickets ya filtrados por búsqueda/tipo/estado/asignado.
 * @param {Object}   props.typeColors         Mapa tipo→clases de color del badge.
 * @param {Object}   props.typeLabels         Mapa tipo→etiqueta legible.
 * @param {Object}   props.priorityColors     Mapa prioridad→clases de color del badge.
 * @param {Object}   props.priorityLabels     Mapa prioridad→etiqueta legible.
 * @param {Object}   props.statusColors       Mapa estado→clases de color del badge.
 * @param {Object}   props.statusLabels       Mapa estado→etiqueta legible.
 * @param {Function} props.formatDate         (dateStr) => string — formatea fechas.
 * @param {Function} props.openDetail         (ticket) => void — abre el modal de detalle.
 * @param {Function} props.canDelete          (resource) => boolean — permiso RBAC para eliminar.
 * @param {Function} props.openDeleteDialog   (event, ticket) => void — abre el diálogo de borrado.
 * @param {boolean}  props.deleteDialogOpen   Apertura del diálogo de borrado.
 * @param {Function} props.setDeleteDialogOpen Setter del flag del diálogo de borrado.
 * @param {Object|null} props.ticketToDelete  Ticket pendiente de borrado (para el texto).
 * @param {Function} props.handleDeleteTicket Confirma el borrado permanente.
 */
export const ITTicketsTable = ({
  filteredTickets,
  typeColors,
  typeLabels,
  priorityColors,
  priorityLabels,
  statusColors,
  statusLabels,
  formatDate,
  openDetail,
  canDelete,
  openDeleteDialog,
  deleteDialogOpen,
  setDeleteDialogOpen,
  ticketToDelete,
  handleDeleteTicket,
}) => {
  return (
    <>
      {/* Tickets Table */}
      <Card className="bg-[#0A0A35] border-[#1E1E5F]">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="border-[#1E1E5F] hover:bg-transparent">
                <TableHead className="text-[#03BEE4]">Número</TableHead>
                <TableHead className="text-[#03BEE4]">Tipo</TableHead>
                <TableHead className="text-[#03BEE4]">Título</TableHead>
                <TableHead className="text-[#03BEE4]">Creado por</TableHead>
                <TableHead className="text-[#03BEE4]">Asignado a</TableHead>
                <TableHead className="text-[#03BEE4]">Prioridad</TableHead>
                <TableHead className="text-[#03BEE4]">Estado</TableHead>
                <TableHead className="text-[#03BEE4]">Fecha</TableHead>
                <TableHead className="text-[#03BEE4]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredTickets.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-slate-400 py-8">
                    No hay tickets para mostrar
                  </TableCell>
                </TableRow>
              ) : (
                filteredTickets.map((ticket) => (
                  <TableRow
                    key={ticket.id}
                    className="border-[#1E1E5F] hover:bg-[#1B1B4B] cursor-pointer"
                    onClick={() => openDetail(ticket)}
                  >
                    <TableCell className="text-[#42E695] font-mono">{ticket.ticket_number}</TableCell>
                    <TableCell>
                      <Badge className={`${typeColors[ticket.ticket_type]} border`}>
                        {ticket.ticket_type === "INCIDENCIA" ? (
                          <AlertTriangle className="h-3 w-3 mr-1" />
                        ) : (
                          <FileText className="h-3 w-3 mr-1" />
                        )}
                        {typeLabels[ticket.ticket_type]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-white max-w-[200px] truncate">{ticket.title}</TableCell>
                    <TableCell className="text-slate-300">{ticket.created_by_name || "-"}</TableCell>
                    <TableCell className="text-slate-300 text-sm">
                      {ticket.assigned_to_name || <span className="text-slate-500 italic">Sin asignar</span>}
                    </TableCell>
                    <TableCell>
                      <Badge className={`${priorityColors[ticket.priority]} text-white`}>
                        {priorityLabels[ticket.priority]}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${statusColors[ticket.status]} text-white`}>
                        {statusLabels[ticket.status]}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-400 text-sm">{formatDate(ticket.created_at)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            openDetail(ticket);
                          }}
                          className="text-[#03BEE4]"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        {canDelete("it_tickets") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => openDeleteDialog(e, ticket)}
                            className="text-red-400 hover:text-red-300 hover:bg-red-500/20"
                            data-testid={`delete-ticket-${ticket.ticket_number}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="bg-[#0A0A35] border-[#1E1E5F]">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">¿Eliminar ticket?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              Esta acción eliminará permanentemente el ticket <span className="text-[#42E695] font-semibold">{ticketToDelete?.ticket_number}</span>.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#1E1E5F] text-slate-300 hover:bg-[#1E1E5F]">
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteTicket}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
